import { useCallback, useEffect, useRef } from 'react';
import type {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  RefObject,
} from 'react';
import { usePreviewPan, usePreviewSetPanning, usePreviewZoom } from '../state/preview-zoom.store';
import { clamp } from '../utils/clamp';
import { useSpaceKeyState } from './useSpaceKeyState';

interface Options {
  containerRef: RefObject<HTMLElement | null>;
  mediaRef: RefObject<HTMLElement | null>;
  enabled: boolean;
}

interface PanGestures {
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
  onContextMenu: (e: ReactMouseEvent<HTMLElement>) => void;
}

interface PanBounds {
  containerW: number;
  containerH: number;
  renderedW: number;
  renderedH: number;
}

function clampPan(
  pan: { x: number; y: number },
  zoom: number,
  bounds: PanBounds,
): { x: number; y: number } {
  const overshootX = Math.max(0, (bounds.renderedW * zoom - bounds.containerW) / 2);
  const overshootY = Math.max(0, (bounds.renderedH * zoom - bounds.containerH) / 2);
  return {
    x: clamp(pan.x, -overshootX, overshootX),
    y: clamp(pan.y, -overshootY, overshootY),
  };
}

export function usePreviewPanGestures({ containerRef, mediaRef, enabled }: Options): PanGestures {
  const { zoom } = usePreviewZoom();
  const { pan, setPan } = usePreviewPan();
  const setPanning = usePreviewSetPanning();

  const enabledRef = useRef(enabled);
  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);
  // Suppress page-scroll when Space is pressed *and* pan is enabled. The shared
  // hook keeps the ref reflecting physical key state regardless of focus.
  const isSpacePressedRef = useSpaceKeyState({
    onPress: (e) => {
      if (enabledRef.current) e.preventDefault();
    },
  });
  const activePointerRef = useRef<number | null>(null);
  const captureTargetRef = useRef<HTMLElement | null>(null);
  const startRef = useRef<{
    clientX: number;
    clientY: number;
    pan: { x: number; y: number };
    bounds: PanBounds;
  } | null>(null);

  useEffect(() => {
    enabledRef.current = enabled;
    zoomRef.current = zoom;
    panRef.current = pan;
  }, [enabled, zoom, pan]);

  // If pan is disabled mid-press (e.g. zoom drops to 1, tool selected), reset state.
  useEffect(() => {
    if (enabled) return;
    const pointerId = activePointerRef.current;
    const target = captureTargetRef.current;
    if (pointerId != null && target) {
      try {
        target.releasePointerCapture(pointerId);
      } catch {
        // Pointer may already be released.
      }
    }
    activePointerRef.current = null;
    captureTargetRef.current = null;
    startRef.current = null;
    setPanning(false);
  }, [enabled, setPanning]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!enabledRef.current) return;
      const isRightClick = e.button === 2;
      const isSpaceLeftClick = e.button === 0 && isSpacePressedRef.current;
      if (!isRightClick && !isSpaceLeftClick) return;
      const containerEl = containerRef.current;
      const mediaEl = mediaRef.current;
      if (!containerEl || !mediaEl) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      activePointerRef.current = e.pointerId;
      captureTargetRef.current = e.currentTarget;
      startRef.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        pan: panRef.current,
        bounds: {
          containerW: containerEl.offsetWidth,
          containerH: containerEl.offsetHeight,
          renderedW: mediaEl.offsetWidth,
          renderedH: mediaEl.offsetHeight,
        },
      };
      setPanning(true);
    },
    [containerRef, mediaRef, setPanning, isSpacePressedRef],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (activePointerRef.current !== e.pointerId) return;
      const start = startRef.current;
      if (!start) return;
      const next = {
        x: start.pan.x + (e.clientX - start.clientX),
        y: start.pan.y + (e.clientY - start.clientY),
      };
      setPan(clampPan(next, zoomRef.current, start.bounds));
    },
    [setPan],
  );

  const endPointer = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (activePointerRef.current !== e.pointerId) return;
      activePointerRef.current = null;
      captureTargetRef.current = null;
      startRef.current = null;
      setPanning(false);
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Pointer may already be released.
      }
    },
    [setPanning],
  );

  const onContextMenu = useCallback((e: ReactMouseEvent<HTMLElement>) => {
    if (!enabledRef.current) return;
    e.preventDefault();
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: endPointer,
    onPointerCancel: endPointer,
    onContextMenu,
  };
}
